/** Shared bits for Dataroom-backed pages: item status pills, dates, downloads, uploads. */
import type { ReactNode } from "react";

export type ItemStatus = "missing" | "expired" | "due" | "expiring" | "current" | "calculated";

export interface Item {
  requirementId: string;
  hotelId: string;
  hotelName: string;
  docType: string;
  label: string;
  folder: string;
  group: string | null;
  period: string | null;
  status: ItemStatus;
  dueOn: string | null;
  feeds?: string;
  document: null | {
    id: string; title: string; fileName: string; issuedBy: string | null; documentDate: string | null;
    expiresOn: string | null; uploadedAt: string; uploadedBy: string | null; version: number;
  };
}

export const FOLDER_LABELS: Record<string, string> = {
  financial_reports: "Financial Reports", reo_documents: "REO Documents", compliance: "Compliance",
  debt_compliance: "Debt Compliance", tax: "Tax",
};
export const FOLDER_KEYS = Object.keys(FOLDER_LABELS);

/** SPEC §8: yellow 30 days before expiry, red when expired or missing. */
const STATUS: Record<ItemStatus, { label: string; color: string; bg: string }> = {
  missing:    { label: "Missing",  color: "#b42318", bg: "#fdecea" },
  expired:    { label: "Expired",  color: "#b42318", bg: "#fdecea" },
  due:        { label: "Due",      color: "#8a5a00", bg: "#fff4e0" },
  expiring:   { label: "Expiring", color: "#8a5a00", bg: "#fff4e0" },
  current:    { label: "Current",  color: "#2f7d4f", bg: "#e9f5ee" },
  calculated: { label: "Calculated by Owners Module", color: "#15263d", bg: "#eef1f6" },
};
export const needsAttention = (s: ItemStatus) => s === "missing" || s === "expired" || s === "due" || s === "expiring";

export function StatusPill({ s, dueOn }: { s: ItemStatus; dueOn?: string | null }) {
  const st = STATUS[s];
  const when = dueOn && (s === "due" || s === "expiring" || s === "expired") ? ` ${shortDate(dueOn)}` : "";
  return <span className="pill" style={{ color: st.color, background: st.bg, borderColor: st.bg }}>{st.label}{when}</span>;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T12:00:00" : iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function periodLabel(p: string | null): string {
  if (!p) return "";
  const m = p.match(/^(\d{4})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const q = p.match(/^(\d{4})-Q(\d)$/);
  if (q) return `Q${q[2]} ${q[1]}`;
  return p;
}

export function DocLinks({ id }: { id: string }) {
  return (
    <span className="row" style={{ gap: 8 }}>
      <a href={`/api/documents/${id}/download?inline=1`} target="_blank" rel="noreferrer">View</a>
      <a href={`/api/documents/${id}/download`}>Get</a>
    </span>
  );
}

/** Colored segment bar: current / needs attention / missing (SPEC §7). */
export function SegmentBar({ items }: { items: Item[] }) {
  const n = items.length || 1;
  const cur = items.filter((i) => i.status === "current" || i.status === "calculated").length;
  const miss = items.filter((i) => i.status === "missing" || i.status === "expired").length;
  const att = items.length - cur - miss;
  const seg = (k: number, c: string) => k ? <div style={{ width: `${(k / n) * 100}%`, background: c }} /> : null;
  return (
    <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: "#eee", width: 160 }}>
      {seg(cur, "#2f7d4f")}{seg(att, "#d9a400")}{seg(miss, "#b42318")}
    </div>
  );
}

export function FilterButtons<T extends string>({ value, options, onChange }: { value: T; options: [T, ReactNode][]; onChange: (v: T) => void }) {
  return (
    <div className="chips" style={{ marginBottom: 14 }}>
      {options.map(([k, label]) => (
        <button key={k} type="button" className={`chip${value === k ? " on" : ""}`} aria-pressed={value === k} onClick={() => onChange(k)}>{label}</button>
      ))}
    </div>
  );
}

/**
 * Upload one file to the Dataroom. Text fields go BEFORE the file — the API
 * reads them first to validate the folder before storing anything.
 */
export async function uploadFile(hotelId: string, file: File, fields: Record<string, string | undefined>): Promise<{ id: string; version: number }> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v) fd.append(k, v);
  fd.append("file", file);
  const r = await fetch(`/api/dataroom/hotels/${hotelId}/files`, { method: "POST", body: fd, credentials: "same-origin", headers: { "x-owners-app": "1" } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `Upload failed (${r.status})`);
  return data.document;
}
